/** @jsxImportSource @emotion/react */
import { Box, Button, useTheme } from '@mui/material'
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@app/components/ui/Dialog/Dialog'
import { toast } from '@app/components/ui/Toast/toast'
import { riskAvailableWorkspacesAtom } from '@app/state/riskAtoms'
import type { RiskWorkspace } from '@app/types'
import { useAtomValue } from 'jotai'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWorkspaceLoader } from './hooks/useWorkspaceLoader'
import { contentAreaStyles } from './WorkspacesDialog.styles'
import WorkspacesDialogLoading from './WorkspacesDialogLoading'
import { WorkspacesMenu } from './WorkspacesMenu/WorkspacesMenu'
import { WorkspaceNavbar } from './WorkspaceNavbar/WorkspaceNavbar'
import { workspaceHasNoValidLayers } from './utils/Workspaces.utils'
import { RootState } from '@app/redux/store'
import { useSelector } from 'react-redux'
import { logDataDogAction } from '@app/utils/logger'
import { DATADOG_ACTIONS } from '@app/app-constants'

const WorkspacesManager = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const workspaces = useAtomValue(riskAvailableWorkspacesAtom)
  const [selectedWorkspace, setSelectedWorkspace] = useState<RiskWorkspace | null>(null)
  const { loadWorkspace } = useWorkspaceLoader()
  const { layerData } = useSelector((state: RootState) => state.riskMap)

  const handleLoadWorkspace = () => {
    if (!selectedWorkspace || !layerData || workspaceHasNoValidLayers(selectedWorkspace, layerData))
      return

    const workspace = selectedWorkspace
    // Close the dialog immediately. The layer pipeline runs in the background
    // and shows its own on-map loading snackbar, so there's no reason to block
    // the modal on the full (network + geometry filtering) load.
    navigate('../..')

    loadWorkspace(workspace)
      .then(() => {
        logDataDogAction(DATADOG_ACTIONS.WORKSPACE_LOADED, { name: workspace.name })
        toast({
          title: (
            <span>
              <b>{workspace.name}</b> is loaded
            </span>
          ),
          duration: 3000,
          type: 'success',
          position: 'top-right',
        })
      })
      .catch((error) => {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to load Workspace',
          duration: 3000,
          type: 'error',
          position: 'top-right',
        })
      })
  }

  const onCancel = () => {
    navigate('../..')
  }

  if (workspaces.isLoading) {
    return <WorkspacesDialogLoading heading={config.heading} description={config.description} />
  }

  if (workspaces.isError) {
    return (
      <WorkspacesDialogLoading heading={config.heading} description={config.description} isError />
    )
  }

  return (
    <>
      <WorkspaceNavbar currentTab="saved" />
      <Box css={contentAreaStyles(theme)}>
        <DialogHeader>
          <DialogTitle>{config.heading}</DialogTitle>
          {config.description && <DialogDescription>{config.description}</DialogDescription>}
        </DialogHeader>

        <WorkspacesMenu
          onWorkspaceSelected={setSelectedWorkspace}
          selectedWorkspace={selectedWorkspace}
        />

        <DialogFooter>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLoadWorkspace}
            disabled={
              !selectedWorkspace ||
              (layerData ? workspaceHasNoValidLayers(selectedWorkspace, layerData) : false)
            }
          >
            Load
          </Button>
        </DialogFooter>
      </Box>
    </>
  )
}

export default WorkspacesManager

// local config, DRY, easier to edit
const config = {
  heading: 'Load a Workspace',
  description: undefined,
}
